import { Injectable } from "@angular/core";

import { InventoryItem, InventoryLocation } from "../inventory";
import { Address } from "../../core/models/address.model";

export interface MachineStructuredDataOptions {
  url: string;
  siteUrl: string;
  siteName: string;
  baseImagePath: string;
  siteLogo?: string;
  currency?: string;
  contextUrl?: string;
}

@Injectable({ providedIn: "root" })
export class MachineStructuredDataService {
  buildStructuredData(machine: InventoryItem, options: MachineStructuredDataOptions): string {
    const normalizedOptions = {
      ...options,
      siteUrl: this.normalizeUrl(options.siteUrl),
      currency: options.currency ?? "USD",
    };

    const organization = this.buildOrganization(normalizedOptions);
    const product = this.buildProduct(machine, normalizedOptions);
    const breadcrumbs = this.buildBreadcrumbs(machine, normalizedOptions);

    const graph = [organization, product, breadcrumbs].filter(Boolean);
    const structuredData = this.prune({
      "@context": "https://schema.org",
      "@graph": graph,
    });

    return JSON.stringify(structuredData, null, 2);
  }

  private buildOrganization(options: MachineStructuredDataOptions) {
    return this.prune({
      "@type": "Organization",
      name: options.siteName,
      url: options.siteUrl,
      logo: options.siteLogo,
    });
  }

  private buildProduct(machine: InventoryItem, options: MachineStructuredDataOptions) {
    const name = this.getProductName(machine);
    const description = this.getDescription(machine);
    const images = this.getImages(machine, options.baseImagePath);
    const availability = this.getAvailability(machine.status);
    const validFrom = this.getValidFrom(machine);
    const seller = this.buildSeller(machine, options);
    const location = this.buildPlace(machine.location);
    const offers = this.buildOffers(machine, options, availability, validFrom, seller, location);

    const aggregateRating = this.buildAggregateRating(machine);

    return this.prune({
      "@type": "Product",
      name,
      description,
      sku: machine.serialNumber || machine.id,
      mpn: machine.serialNumber,
      model: machine.model,
      brand: this.prune({
        "@type": "Brand",
        name: machine.make,
      }),
      category: machine.primaryType,
      url: options.url,
      image: images,
      manufacturer: this.prune({
        "@type": "Organization",
        name: machine.make,
      }),
      releaseDate: this.getReleaseDate(machine.year),
      itemCondition: "https://schema.org/UsedCondition",
      offers,
      aggregateRating,
    });
  }

  private buildAggregateRating(machine: InventoryItem) {
    const rating = machine.rating ?? machine.ratingInfo?.machine ?? machine.ratingInfo?.overall;
    const reviewCount = (machine as any)?.numberOfReviews;

    if (!rating || rating <= 0 || !reviewCount || reviewCount <= 0) {
      return undefined;
    }

    return this.prune({
      "@type": "AggregateRating",
      ratingValue: this.roundTo(rating, 1),
      reviewCount,
    });
  }

  private buildBreadcrumbs(machine: InventoryItem, options: MachineStructuredDataOptions) {
    const name = this.getProductName(machine);

    // Determine URL prefix from context URL or fallback to machine properties
    let urlPrefix = '/equipment-rental'; // Default fallback
    let sectionName = 'Equipment Rentals';

    if (options.contextUrl) {
      if (options.contextUrl.startsWith('/equipment-for-sale')) {
        urlPrefix = '/equipment-for-sale';
        sectionName = 'Equipment For Sale';
      } else if (options.contextUrl.startsWith('/equipment-rental')) {
        urlPrefix = '/equipment-rental';
        // Check for rpoEnabled query param in context
        if (options.contextUrl.includes('rpoEnabled=true')) {
          sectionName = 'Rent to Own';
        } else {
          sectionName = 'Equipment Rentals';
        }
      } else if (options.contextUrl.startsWith('/equipment-rentals')) {
        urlPrefix = '/equipment-rentals';
        // Check for rpoEnabled query param in context
        if (options.contextUrl.includes('rpoEnabled=true')) {
          sectionName = 'Rent to Own';
        } else {
          sectionName = 'Equipment Rentals';
        }
      } else if (options.contextUrl.startsWith('/equipment')) {
        urlPrefix = '/equipment';
        sectionName = 'Equipment';
      }
    } else {
      // Fallback to machine properties if no context
      if (machine.buyItNowOnly) {
        urlPrefix = '/equipment-for-sale';
        sectionName = 'Equipment For Sale';
      } else if (!machine.rentalRate && !machine.rpoEnabled && machine.buyItNowEnabled) {
        // Only buy-it-now, no rental or RPO
        urlPrefix = '/equipment-for-sale';
        sectionName = 'Equipment For Sale';
      } else if (machine.rpoEnabled && !machine.buyItNowEnabled) {
        // Rent-to-own enabled, but no buy-it-now (may or may not have rental)
        urlPrefix = '/equipment-rental';
        sectionName = 'Rent to Own';
      } else if (machine.rentalRate > 0 && !machine.rpoEnabled && !machine.buyItNowEnabled) {
        // Only rental, no RPO or buy-it-now
        urlPrefix = '/equipment-rental';
        sectionName = 'Equipment Rentals';
      }
      // Otherwise use /equipment-rental (mixed availability or all three options)
    }

    const categoryUrl = `${options.siteUrl}${urlPrefix}?primaryType=${encodeURIComponent(machine.primaryType || "")}`;

    return this.prune({
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: options.siteName,
          item: options.siteUrl,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: sectionName,
          item: `${options.siteUrl}${urlPrefix}`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: machine.primaryType,
          item: categoryUrl,
        },
        {
          "@type": "ListItem",
          position: 4,
          name,
          item: options.url,
        },
      ],
    });
  }

  private buildOffers(
    machine: InventoryItem,
    options: MachineStructuredDataOptions,
    availability: string,
    validFrom: string,
    seller: any,
    location: any,
  ) {
    const offers = [
      this.buildRentalOffer(machine.rentalRate, "MONTH", machine, options, availability, validFrom, seller, location),
      this.buildRentalOffer(machine.rentalRateWeekly, "WEEK", machine, options, availability, validFrom, seller, location),
      this.buildRentalOffer(machine.rentalRateDaily, "DAY", machine, options, availability, validFrom, seller, location),
      this.buildRentToOwnOffer(machine, options, availability, validFrom, seller, location),
      this.buildPurchaseOffer(machine, options, availability, validFrom, seller, location),
    ];

    return offers.filter(Boolean);
  }

  private buildRentalOffer(
    price: number,
    unit: "DAY" | "WEEK" | "MONTH",
    machine: InventoryItem,
    options: MachineStructuredDataOptions,
    availability: string,
    validFrom: string,
    seller: any,
    location: any,
  ) {
    if (!price || price <= 0) {
      return undefined;
    }

    return this.prune({
      "@type": "Offer",
      name: `${unit.toLowerCase()} rental`,
      price: this.roundTo(price, 2),
      priceCurrency: options.currency,
      availability,
      url: options.url,
      validFrom,
      itemCondition: "https://schema.org/UsedCondition",
      seller,
      availableAtOrFrom: location,
      priceSpecification: this.prune({
        "@type": "UnitPriceSpecification",
        priceCurrency: options.currency,
        price: this.roundTo(price, 2),
        unitText: unit,
      }),
    });
  }

  private buildRentToOwnOffer(
    machine: InventoryItem,
    options: MachineStructuredDataOptions,
    availability: string,
    validFrom: string,
    seller: any,
    location: any,
  ) {
    if (!machine.rpoEnabled) {
      return undefined;
    }

    const price = machine.purchasePrice || machine.buyItNowPrice;
    if (!price || price <= 0) {
      return undefined;
    }

    return this.prune({
      "@type": "Offer",
      name: "Rent to Own",
      price: this.roundTo(price, 2),
      priceCurrency: options.currency,
      availability,
      url: options.url,
      validFrom,
      itemCondition: "https://schema.org/UsedCondition",
      seller,
      availableAtOrFrom: location,
      additionalProperty: [
        this.prune({
          "@type": "PropertyValue",
          name: "Rent to Own",
          value: "Available",
        }),
      ],
    });
  }

  private buildPurchaseOffer(
    machine: InventoryItem,
    options: MachineStructuredDataOptions,
    availability: string,
    validFrom: string,
    seller: any,
    location: any,
  ) {
    if (!(machine.buyItNowEnabled || machine.buyItNowOnly)) {
      return undefined;
    }

    if (machine.buyItNowPricingOnRequest) {
      return undefined;
    }

    const price = machine.buyItNowPrice || machine.purchasePrice;
    if (!price || price <= 0) {
      return undefined;
    }

    return this.prune({
      "@type": "Offer",
      name: "Buy It Now",
      price: this.roundTo(price, 2),
      priceCurrency: options.currency,
      availability,
      url: options.url,
      validFrom,
      itemCondition: "https://schema.org/UsedCondition",
      seller,
      availableAtOrFrom: location,
    });
  }

  private buildSeller(machine: InventoryItem, options: MachineStructuredDataOptions) {
    const sellerName = machine.accountSummary?.name || machine.account?.name || options.siteName;
    return this.prune({
      "@type": "Organization",
      name: sellerName,
    });
  }

  private buildPlace(location: InventoryLocation) {
    if (!location) {
      return undefined;
    }

    const address = this.buildAddress(location.address);

    return this.prune({
      "@type": "Place",
      name: location.name,
      geo: this.buildGeo(location),
      address,
    });
  }

  private buildGeo(location: InventoryLocation) {
    const latitude = this.toNumber(location.latitude);
    const longitude = this.toNumber(location.longitude);

    if (latitude === undefined || longitude === undefined) {
      return undefined;
    }

    return this.prune({
      "@type": "GeoCoordinates",
      latitude,
      longitude,
    });
  }

  private buildAddress(address: Address) {
    if (!address) {
      return undefined;
    }

    const streetAddress = [address.address1, address.address2].filter(Boolean).join(", ");

    return this.prune({
      "@type": "PostalAddress",
      streetAddress: streetAddress || undefined,
      addressLocality: address.city,
      addressRegion: address.stateProvince,
      postalCode: address.postalCode,
      addressCountry: address.countryCode || address.country,
    });
  }

  private getProductName(machine: InventoryItem): string {
    const parts = [machine.year, machine.make, machine.model, machine.primaryType].filter(Boolean);
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }

  private getDescription(machine: InventoryItem): string {
    if (machine.description) {
      const text = machine.description.replace(/<[^>]+>/g, " ");
      return text.replace(/\s+/g, " ").trim();
    }

    const name = this.getProductName(machine);
    const cityState = [machine.location?.address?.city, machine.location?.address?.stateProvince]
      .filter(Boolean)
      .join(", ");
    const locationText = cityState ? ` in ${cityState}` : "";
    return `Discover ${name}${locationText} available through Rubbl.`;
  }

  private getImages(machine: InventoryItem, baseImagePath: string): string[] {
    const images = machine.images || machine.thumbnails || [];
    if (!images.length) {
      return [];
    }

    return images.map(image => this.normalizeUrl(`${baseImagePath}${image}`));
  }

  private getAvailability(status: InventoryItem["status"]): string {
    switch (status) {
      case "Available":
        return "https://schema.org/InStock";
      case "Onboarding":
        return "https://schema.org/PreOrder";
      case "OnRent":
      case "Pending":
        return "https://schema.org/OutOfStock";
      default:
        return "https://schema.org/LimitedAvailability";
    }
  }

  private getValidFrom(machine: InventoryItem): string {
    const date = machine.auditMetadata?.modifiedDateTime || machine.auditMetadata?.createdDateTime;
    if (date) {
      const parsed = new Date(date);
      if (!isNaN(parsed.valueOf())) {
        return parsed.toISOString();
      }
    }
    return new Date().toISOString();
  }

  private getReleaseDate(year: string | number) {
    if (!year) {
      return undefined;
    }

    const numericYear = Number(year);
    if (!isNaN(numericYear) && numericYear > 1900) {
      return `${numericYear}-01-01`;
    }

    return undefined;
  }

  private normalizeUrl(url: string): string {
    if (!url) {
      return url;
    }
    return url.replace(/([^:]\/{2,})/g, (match, p1) => p1.replace(/\/{2,}/g, "/"));
  }

  private toNumber(value: string | number) {
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string" && value.trim().length) {
      const parsed = Number(value);
      return isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }

  private roundTo(value: number, precision: number): number {
    const multiplier = Math.pow(10, precision);
    return Math.round(value * multiplier) / multiplier;
  }

  private prune<T>(value: T): T {
    if (Array.isArray(value)) {
      return value
        .map(item => this.prune(item))
        .filter(item => {
          if (item === undefined || item === null) {
            return false;
          }
          if (Array.isArray(item)) {
            return item.length > 0;
          }
          if (typeof item === "object") {
            return Object.keys(item as Record<string, unknown>).length > 0;
          }
          if (typeof item === "string") {
            return item.trim().length > 0;
          }
          return true;
        }) as unknown as T;
    }

    if (value && typeof value === "object") {
      const result: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([key, entryValue]) => {
        if (entryValue === undefined || entryValue === null) {
          return;
        }
        if (typeof entryValue === "string" && entryValue.trim().length === 0) {
          return;
        }
        const prunedValue = this.prune(entryValue);
        if (prunedValue === undefined || prunedValue === null) {
          return;
        }
        if (Array.isArray(prunedValue) && prunedValue.length === 0) {
          return;
        }
        if (typeof prunedValue === "object" && Object.keys(prunedValue as Record<string, unknown>).length === 0) {
          return;
        }
        result[key] = prunedValue;
      });
      return result as T;
    }

    return value;
  }
}

